import {ActivityCommonProperties, DurationProps} from "../../types-enums/activity-common-properties";
import {getActivitiesByDay} from "./activities";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray, TRAVEL_OPTION_TYPE} from "../../types-enums/activity";
import {roundCo2E} from "../math/round-values";
import {getDurationInDaysOrNights} from "./common";
import {createArrayFromNumber} from "../common";

export type Co2KgEPerTravelOptionProps = Record<TRAVEL_OPTION_TYPE, number>
export type Co2KgEPerActivityProps = Record<ACTIVITY_TYPE, number>

/**
 * Calculates the total CO2 emissions (in kilograms equivalent) for a journey
 * by summing up the emissions for each day of the journey.
 *
 * @param {AllActivityTypesArray} activities - Array of activities associated with the journey.
 * @param {DurationProps} journeyDuration - The duration of the journey, including start and end dates.
 * @param {boolean} round - Indicates if the CO2 emissions per day should be rounded during calculation.
 * @returns {number} The total CO2 emissions for the journey in kilograms equivalent.
 */
export const getTotalCo2KgEForJourneyViaSumOfDays = (activities: AllActivityTypesArray, journeyDuration: DurationProps, round: boolean) => {
    const journeyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
    let totalJourneyEmissions = getTotalCo2KgEForActivities(activities, true)
    // its better to use the rounded value per journey day if possible
    if (journeyDurationInDays !== null) totalJourneyEmissions = createArrayFromNumber(journeyDurationInDays)
        .reduce((sum, day) => sum + getCo2EForDayOfJourney(activities, day, round), 0)
    return totalJourneyEmissions
}


/**
 * Calculates the total CO2 emissions for all activities in the provided array.
 *
 * @param {AllActivityTypesArray} activities - An array of all activity data types to calculate emissions for.
 * @param {boolean} round - Specifies whether the CO2 emissions should be rounded.
 * @returns {number} The total CO2 emissions for all provided activities.
 */
export const getCo2EForAllActivities = (activities: AllActivityTypesArray, round: boolean): number => {
    let co2E = 0;
    activities.forEach(activity => {
        const result = sumActivitiesEmissionResponseTotalEmissionsKgCo2(activity, round)
        if (result !== null) {
            if (round) {
                co2E += roundCo2E(result)
            } else {
                co2E += result
            }
        }
    })
    return co2E
}

/**
 * Calculates the total CO2e (carbon dioxide equivalent) emissions for a given day of a journey.
 *
 * @param {AllActivityTypesArray} activities - Array containing all activity types.
 * @param {ActivityCommonProperties["common"]["day"]} day - The specific day for which emissions are calculated.
 * @param {boolean} round - Determines whether to round the emissions to the nearest whole number.
 * @returns {number} The total CO2e emissions for the specified day, either rounded or unrounded based on the given flag.
 */
export const getCo2EForDayOfJourney = (activities: AllActivityTypesArray, day: ActivityCommonProperties["common"]["day"], round: boolean): number => {
    let co2E = 0;
    const activitiesByDay = getActivitiesByDay(activities, day)
    activitiesByDay.forEach(activity => {
        const emissionKgCO2eTotal = sumActivitiesEmissionResponseTotalEmissionsKgCo2(activity, round)
        if (emissionKgCO2eTotal !== null) {
            if (round) {
                co2E += roundCo2E(emissionKgCO2eTotal)
            } else {
                co2E += emissionKgCO2eTotal
            }
        }
    })
    return co2E
}

/**
 * Calculates the total CO2 emissions in kilograms for each activity type from a list of activities.
 *
 * @param {AllActivityTypesArray} activities - Array of activity objects containing emission data and activity types.
 * @param {boolean} round - A flag indicating whether the CO2 emissions should be rounded to the nearest integer.
 * @returns {Record<ACTIVITY_TYPE, number>} A mapping of activity types to their total CO2 emissions in kilograms.
 */
export const getCo2KgEForActivityType = (activities: AllActivityTypesArray, round: boolean): Record<ACTIVITY_TYPE, number> => {
    const emissions: Co2KgEPerActivityProps = {} as Co2KgEPerActivityProps;
    activities.forEach(activity => {
        const {common: {activityType}} = activity
        const emissionKgCO2eTotal = sumActivitiesEmissionResponseTotalEmissionsKgCo2(activity, round)
        if (emissionKgCO2eTotal !== null && emissionKgCO2eTotal > 0) {
            if (!emissions[activityType]) {
                if (round) {

                    emissions[activityType] = roundCo2E(emissionKgCO2eTotal)
                } else {
                    emissions[activityType] = emissionKgCO2eTotal
                }
            } else {
                if (round) {

                    emissions[activityType] += roundCo2E(emissionKgCO2eTotal)
                } else {
                    emissions[activityType] += emissionKgCO2eTotal
                }
            }
        }
    })
    return emissions;
}

/**
 * Calculates the total CO2 emissions (in kilograms) for all travel option types across a given array of activities.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects containing travel details and emission information.
 * @param {boolean} round - A flag indicating whether the emission values should be rounded to the nearest whole number.
 * @returns {Co2KgEPerTravelOptionProps} An object where the keys represent travel option types, and the values represent the total CO2 emissions (in kilograms) for those travel options.
 */
export const getCo2KgEForAllTravelOptionTypes = (activities: AllActivityTypesArray, round: boolean): Co2KgEPerTravelOptionProps => {
    const emissions: Co2KgEPerTravelOptionProps = {} as Co2KgEPerTravelOptionProps;
    activities.forEach(activity => {
        const {common: {travelOptionType}} = activity

        const emissionKgCO2eTotal = sumActivitiesEmissionResponseTotalEmissionsKgCo2(activity, round)
        if (emissionKgCO2eTotal !== null && emissionKgCO2eTotal > 0) {
            if (!emissions[travelOptionType]) {
                if (round) {

                    emissions[travelOptionType] = roundCo2E(emissionKgCO2eTotal)
                } else {
                    emissions[travelOptionType] = emissionKgCO2eTotal
                }
            } else {
                if (round) {

                    emissions[travelOptionType] += roundCo2E(emissionKgCO2eTotal)
                } else {
                    emissions[travelOptionType] += emissionKgCO2eTotal
                }
            }
        }
    })
    return emissions;
}

/**
 * Calculates the total CO2 emissions in kilograms for a specific travel option type from a given array of activities.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects containing emissions data and related properties.
 * @param {TRAVEL_OPTION_TYPE} givenTravelOptionType - The specific type of travel option for which CO2 emissions are calculated.
 * @param {boolean} round - A boolean flag indicating whether to round the calculated CO2 emissions value.
 * @returns {number} The total amount of CO2 emissions (in kilograms) for the given travel option type.
 */
export const getCo2KgEForTravelOptionType = (activities: AllActivityTypesArray, givenTravelOptionType: TRAVEL_OPTION_TYPE, round: boolean) => {
    let emissions = 0
    activities.forEach(activity => {
        const {common: {travelOptionType}} = activity
        if (travelOptionType === givenTravelOptionType) {
            const emissionKgCO2eTotal = sumActivitiesEmissionResponseTotalEmissionsKgCo2(activity, round)
            if (emissionKgCO2eTotal !== null) emissions += roundCo2E(emissionKgCO2eTotal)
        }
    })
    return emissions
}

/**
 * Calculates the total CO2 emissions (in kilograms) for a given activity.
 *
 * This function iterates through the emissions data within the provided activity object
 * and computes the total emissions. If the emissions data contains valid CO2 emission
 * values, the function sums these values up. If the round parameter is set to true, each
 * emission value is rounded before being added to the total. If there are no valid emission
 * values, the function returns null.
 *
 * @param {AllActivityTypes} activity - The activity object containing emissions data.
 * @param {boolean} round - A flag indicating whether the CO2 emissions should be rounded
 * before summation.
 * @returns {number | null} The total emissions in kilograms of CO2 if valid data exists,
 * or null if no valid emissions data is found.
 */
export const sumActivitiesEmissionResponseTotalEmissionsKgCo2 = (activity: AllActivityTypes, round: boolean,): number | null => {
    const {common: {totalEmissions: emissions}} = activity
    if (emissions == null) return null
    let totalEmissions = 0
    let hasValidEmissions = false
    emissions.forEach(emission => {
        if (emission.emissionKgCO2e !== null) {
            if (round) {
                totalEmissions += roundCo2E(emission.emissionKgCO2e)
            } else {
                totalEmissions += emission.emissionKgCO2e
            }
            hasValidEmissions = true
        }
    })
    return hasValidEmissions ? totalEmissions : null;
}

/**
 * Calculates the total CO2 emissions (in kilograms) for a given array of activities.
 *
 * This function iterates over an array of activity objects, extracting and summing
 * the total emissions from each activity. If the activities contain emissions data,
 * it computes the total emissions while optionally rounding to the nearest whole number.
 *
 * @param {AllActivityTypesArray} activities - An array of activity objects, each containing emissions data.
 * @param {boolean} round - A flag indicating whether to round the CO2 emissions to the nearest whole number.
 * @returns {number} The total CO2 emissions in kilograms. Returns 0 if no activities contain emissions data.
 */
export const getTotalCo2KgEForActivities = (activities: AllActivityTypesArray, round: boolean) => {
    let summedEmissions = 0
    activities.forEach(activity => {
        const {common: {totalEmissions}} = activity
        if (totalEmissions !== null) {
            const calculatedTotalEmissions = sumActivitiesEmissionResponseTotalEmissionsKgCo2(activity, round)
            if (calculatedTotalEmissions !== null) {
                if (round) {

                    summedEmissions += roundCo2E(calculatedTotalEmissions)
                } else {
                    summedEmissions += calculatedTotalEmissions
                }
            }
        }
    })
    return summedEmissions
}